import React, { useState, useRef } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { auth } from '../firebase';
import ReCAPTCHA from "react-google-recaptcha";

function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const onChangeCaptcha = (token) => {
    setCaptchaToken(token);
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!captchaToken) {
      setError('Please verify that you are not a robot');
      return;
    }

    setLoading(true);

    console.log('🔐 Starting email authentication...');
    console.log('Mode:', isLogin ? 'Login' : 'Sign Up');
    console.log('Email:', email);
    console.log('Remember Me:', rememberMe);

    try {
      if (isLogin) {
        console.log('Setting persistence...');
        // Set persistence based on remember me checkbox
        await setPersistence(
          auth, 
          rememberMe ? browserLocalPersistence : browserSessionPersistence
        );
        console.log('✅ Persistence set successfully');
        
        console.log('Attempting sign in...');
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log('✅ Sign-in successful:', userCredential.user.email);
      } else {
        console.log('Attempting account creation...');
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        console.log('✅ Account created successfully:', userCredential.user.email);
      }
    } catch (err) {
      console.error('❌ Email authentication error:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      
      // Provide user-friendly error messages
      let userMessage = err.message;
      if (err.code === 'auth/invalid-credential') {
        userMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (err.code === 'auth/user-not-found') {
        userMessage = 'No account found with this email. Please sign up first.';
      } else if (err.code === 'auth/wrong-password') {
        userMessage = 'Incorrect password. Please try again.';
      } else if (err.code === 'auth/email-already-in-use') {
        userMessage = 'An account with this email already exists. Please sign in instead.';
      } else if (err.code === 'auth/weak-password') {
        userMessage = 'Password is too weak. Please use at least 6 characters.';
      } else if (err.code === 'auth/invalid-email') {
        userMessage = 'Invalid email address. Please check and try again.';
      } else if (err.code === 'auth/network-request-failed') {
        userMessage = 'Network error. Please check your internet connection and try again.';
      }
      
      setError(userMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    
    console.log('🔐 Starting Google authentication...');
    console.log('Current URL:', window.location.href);
    console.log('Auth object:', auth ? 'Initialized' : 'NOT INITIALIZED');
    
    try {
      const provider = new GoogleAuthProvider();
      console.log('✅ GoogleAuthProvider created');
      
      // Force account selection
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      console.log('✅ Custom parameters set');
      
      console.log('Opening popup for authentication...');
      // Use popup - Firebase will handle persistence automatically
      const result = await signInWithPopup(auth, provider);
      console.log('✅ Google Sign-in successful:', result.user.email);
      console.log('User UID:', result.user.uid);
      // Auth state will update automatically via useAuthState
    } catch (err) {
      console.error('❌ Google Sign-in Error:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      console.error('Full error object:', err);
      
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled. Please try again.');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Pop-up was blocked. Please allow pop-ups for this site and try again.');
      } else if (err.code === 'auth/cancelled-popup-request') {
        setError('Another sign-in is in progress. Please wait.');
      } else if (err.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized. Please contact support.');
      } else {
        setError(`Sign-in failed: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 to-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <img 
            src="/av_icon_v1_gradient_padding.png" 
            alt="AstraVenture Logo" 
            className="w-20 h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isLogin ? 'Discover the Power of AI' : 'Create Your Account'}
          </h1>
          <p className="text-gray-600">
            {isLogin ? 'Sign in to explore and test our AI solutions' : 'Sign up to start your journey'}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleEmailAuth} className="space-y-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              placeholder="your@email.com"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              placeholder="••••••••"
              disabled={loading}
            />
          </div>

          {isLogin && (
            <div className="flex items-center">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                disabled={loading}
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                Remember me
              </label>
            </div>
          )}

          <div className="flex justify-center py-2">
            <ReCAPTCHA
              ref={captchaRef}
              sitekey="6Ld_U08sAAAAAGPi8bWoJtaKbDC6ukZ0_pSzbQNM"
              onChange={onChangeCaptcha}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !captchaToken}
            className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-200 hover:shadow-blue-300"
          >
            {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Sign Up'}
          </button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        {/* Google Sign In */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 rounded-lg border-2 border-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in with Google
        </button>

        {/* Toggle Login/Signup */}
        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setError('');
              setCaptchaToken(null);
              captchaRef.current?.reset();
            }}
            className="text-primary hover:text-primary-dark font-medium text-sm"
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Auth;
